import { PersonalData } from './components/PersonalData';
import { Icon, Text } from '../../foundations';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import { ApisFormProps, SelectOptions } from './ApisForm.model';
import { Button } from '../../navigation/button/Button';
import { useApisForm } from './hooks/UseApisForm';
import { PassengerAddress } from './components/PassengerAddress';
import { ContactData } from './components/ContactData';
import { DocumentationRequired } from './components/DocumentationRequired/DocumentationRequired';
import styles from './ApisForm.module.css';
import { PassengerInfant } from './components/PassengerInfant/PassengerInfant';
import { useEffect } from 'react';
import { CheckBoxInfo } from './components/CheckBoxInfo/CheckBoxInfo';
import { AlertError } from './components/AlertError';
import { containsApisError, controlApisErrors, getFullName } from './ApisForm.utils';
import { Divider } from '../Divider';
import { withProviders } from '../../../providers/with-providers';

const ApisFormComponent: React.FC<ApisFormProps> = ({
	listNationalities,
	listCountries,
	listStates,
	countryCodes,
	model,
	isBlockForm,
	isMultiPassengers,
	onSubmit
}: ApisFormProps) => {
	const {
		options,
		ApisFormContext,
		methods,
		requiredFields,
		isFormDisabled,
		isSubmitDisabled,
		apisErrors,
		showAlertErrors,
		setShowAlertErrors,
		setIsSubmitDisabled,
		onSubmitApisForm,
		setIsFormDisabled
	} = useApisForm({
		listNationalities,
		listCountries,
		listStates,
		countryCodes,
		onSubmit,
		model
	});

	const {
		typePassenger,
		passengerAddress,
		documentationRequired,
		infant,
		isPrivacyStatement = false,
		gdpr,
		id,
		bookingInfoPassenger,
		contactData
	} = model;
	const { t } = useTranslation(['common', 'apis-error']);
	const watchFields: (SelectOptions | string | boolean | undefined)[] = methods.watch(requiredFields);
	const existInfant = infant ? true : false;

	useEffect(() => {
		const areAllFieldsFilled = watchFields.every((field) => {
			if (field && Object.hasOwn(field as SelectOptions, 'value')) return (field as SelectOptions).value;
			return field;
		});
		setIsSubmitDisabled(!areAllFieldsFilled);
	}, [watchFields]);

	useEffect(() => {
		if (Array.isArray(apisErrors) && apisErrors.length > 0) {
			controlApisErrors(apisErrors, methods.setError, t, existInfant);
			setShowAlertErrors(containsApisError(apisErrors));
		}
	}, [apisErrors, t]);

	useEffect(() => {
		if (isBlockForm) {
			setIsFormDisabled(isBlockForm);
			setIsSubmitDisabled(isBlockForm);
			setShowAlertErrors(!isBlockForm);
		}
	}, [isBlockForm]);

	return (
		<div className={styles.box_container}>
			<ApisFormContext.Provider
				value={{
					listOptions: options,
					passengerAddress,
					documentationRequired,
					infant,
					isFormDisabled,
					isPrivacyStatement,
					apisErrors,
					gdpr,
					id,
					bookingInfoPassenger,
					contactData,
					isMultiPassengers
				}}
			>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(onSubmitApisForm)} className={styles.box_content} autoComplete="off">
						{showAlertErrors && <AlertError id={String(id)} errors={apisErrors ?? []} />}

						{infant && (
							<div className={styles.box_title}>
								<Icon name="perfil" className={styles.box_title_icon} />
								<Text as="h4" font="iberia-headline" className={styles.box_title_text}>
									{getFullName({ bookingInfoPassenger })}
								</Text>
							</div>
						)}
						<PersonalData typePassenger={typePassenger} />
						<DocumentationRequired />
						{passengerAddress && <PassengerAddress />}
						<ContactData />

						{/* INFANT*/}
						{infant && <PassengerInfant />}

						<CheckBoxInfo />

						{!isFormDisabled && (
							<>
								<Divider variant="solid-light-gray" className={styles.box_divider} />
								<div className={styles.box_button}>
									<Button
										type="submit"
										data-testid="apis-form-submit"
										disabled={isSubmitDisabled}
										variant={isSubmitDisabled ? 'gray' : 'primary'}
									>
										{t('common:BTN_SAVE_AND_CONTINUE')}
									</Button>
								</div>
							</>
						)}
					</form>
				</FormProvider>
			</ApisFormContext.Provider>
		</div>
	);
};
ApisFormComponent.displayName = 'ApisForm';
export const ApisForm = withProviders(ApisFormComponent);
